import { z } from 'zod';

import { doc } from './openapiHelpers';

export const webhookTypeV21Schema = z.enum([
    'CREATE_ORDER',
    'CREATE_PRODUCT',
    'CREATE_RECEIVING',
    'CREATE_RETURN',
    'CREATE_STOCK_MOVEMENT_COMPLETITION',
    'CREATE_STOCK_MOVEMENT_STOCK_CORRECTION',
    'CREATE_STOCK_MOVEMENT_ORDER',
    'CREATE_STOCK_MOVEMENT_RETURN',
    'CREATE_STOCK_MOVEMENT_RECEIVING',
    'CREATE_STOCK_MOVEMENT_QUALITY_HOLD',
    'CREATE_STOCK_MOVEMENT_QUALITY_RELEASE',
    'UPDATE_ORDER',
    'UPDATE_ORDER_STATUS',
    'UPDATE_PRODUCT',
    'UPDATE_RECEIVING',
    'UPDATE_RECEIVING_STATUS',
    'UPDATE_RETURN',
    'UPDATE_RETURN_STATUS',
]).describe('Webhook subscription event — GWMS POSTs a payload to your url when this event fires.');

export const webhookParametersV21Schema = z.object({
    withPackingLists: z.boolean().optional().describe(
        'Allowed only for UPDATE_ORDER and UPDATE_ORDER_STATUS. Sends orderProducts[].packingLists like GET /orders/{eshopOrderId}?withPackingLists=true.',
    ),
}).strict().nullable();

export const createWebhookV21Schema = z.object({
    url: doc(z.string().min(1), 'HTTPS endpoint that receives webhook POST requests.'),
    webhookType: webhookTypeV21Schema,
    active: doc(z.boolean(), 'Whether the subscription is active.'),
    method: doc(z.enum(['POST', 'GET']), 'HTTP method GWMS uses when calling your URL.'),
    parameters: doc(
        webhookParametersV21Schema.optional(),
        'Optional event-specific flags (e.g. withPackingLists for order webhooks).',
    ),
    payloadVersion: z.enum(['v2', 'v21']).optional().describe(
        'Format of the JSON body POSTed to your URL. Set when you create or update the webhook; if omitted, defaults to v2. v2 uses decimal strings for money and counts; v21 uses JSON numbers (same shapes as GWMS API v2.1).',
    ),
}).strict();

export const updateWebhookV21Schema = createWebhookV21Schema;

export const webhookV21Schema = createWebhookV21Schema.extend({
    id: doc(z.number().int(), 'Internal webhook subscription ID.'),
    registeredAt: doc(z.string().nullable().optional(), 'When the webhook was registered.'),
    unregisteredAt: doc(z.string().nullable().optional(), 'When the webhook was deactivated/deleted.'),
}).strict();

export const webhookWithSecretV21Schema = webhookV21Schema.extend({
    secret: z.string().optional().describe(
        'Shared secret for X-Skladon-Signature verification. Returned only on create or regenerate-secret — store it safely.',
    ),
}).strict();

export type CreateWebhookV21 = z.infer<typeof createWebhookV21Schema>;
export type WebhookV21 = z.infer<typeof webhookV21Schema>;
